/**
 * Outcomes of coding-session tool approvals settled while this window is
 * open, by approval id.
 *
 * Fed by the `direct_session.approval_settled` browser event through the
 * frame reducer (every window applies it, so the store is not mirrored). A
 * card posted in a chat reads it before its stored status, which is only
 * fresh after a reload.
 */
import { create } from "zustand";
import type { DirectApprovalStatus } from "@/lib/direct-approval-notice";

interface DirectApprovalSettledState {
  settled: Record<string, DirectApprovalStatus>;
  apply: (data: Record<string, unknown>) => void;
}

const SETTLED: readonly DirectApprovalStatus[] = [
  "allowed",
  "denied",
  "expired",
];

export const useDirectApprovalSettledStore =
  create<DirectApprovalSettledState>()((set) => ({
    settled: {},
    apply: (data) => {
      const approvalId = data.approval_id;
      const status = SETTLED.find((value) => value === data.status);
      if (typeof approvalId !== "string" || !approvalId || !status) return;
      set((state) => ({ settled: { ...state.settled, [approvalId]: status } }));
    },
  }));

/** The approval's live outcome, if it settled while this window was open. */
export function useDirectApprovalSettled(
  approvalId: string,
): DirectApprovalStatus | undefined {
  return useDirectApprovalSettledStore((state) => state.settled[approvalId]);
}
